import _ from "lodash";
import React, { useEffect, useState } from "react";
import Button from "../../components/buttons";
import { Modal } from "react-bootstrap";
import { useFilterContext } from "../../hooks/FilterContext";
import { DateInput, FormCheck, FormSelect } from "../../components/forms";
import { GenericAdviserSelect, MasterAccountSelect, MasterAccountStatusSelect, ServiceAgreementsSelect } from "../../components/forms/selects";
import ThemedModal from "../../components/utilities/ThemedModal";


export const ClientServiceTermsManagementFiltersModalButton = ({
    className,
    variant = "primary",
    ...rest
}) => {
    return (
        <div className={className}>
            <Button variant={variant} {...rest}>
                Filter Service Terms
            </Button>
        </div>
    );
};

export const ClientServiceTermsManagementFiltersModal = ({
    handleClose,
    onFiltersChanged,
    show,
    ...rest
}) => {
    const { defaultFilters, filter, setFilter } = useFilterContext();
    const [tempFilters, setTempFilters] = useState(filter);

    const onApplyFilters = () => {
        setFilter((original) => {
            return {
                ...original,
                ...tempFilters,
            };
        });
        onClose();
    };

    const onClearFilters = () => {
        setTempFilters((_) => defaultFilters);
    };

    const onFilterValueChanged = (property, value) => {
        setTempFilters((original) => {
            return {
                ...original,
                [property]: value,
            };
        });
    };

    const onClose = () => {

        setTempFilters((original) => {
            return {
                ...original,
                ...filter
            };
        });


        if (handleClose && typeof handleClose === "function") {
            handleClose();
        }
    };

    useEffect(() => {
        if (!_.isEqual(filter, tempFilters)) {
            setTempFilters((_) => filter);
        }
    }, [filter]);
    
    return (
        <Modal size="lg" centered backdrop="static" show={show}>
            <ThemedModal.Header>
                <Modal.Title>Service Term Filters</Modal.Title>
            </ThemedModal.Header>
            <Modal.Body {...rest}>
                <ClientServiceTermsManagementFilters
                    filter={tempFilters}
                    setFilter={onFilterValueChanged}
                />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="warning" onClick={onClearFilters}>
                    Clear Filters
                </Button>
                <Button variant="success" onClick={onApplyFilters}>
                    Apply Filters
                </Button>
                <Button variant="light" onClick={onClose}>
                    Cancel
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const ClientServiceTermsManagementFilters = ({ filter, setFilter }) => {
    const {
        adviserIds,
        masterAccountIds,
        masterAccountStatusIds,
        serviceTermIds,
        serviceTermStatusIds,
        consentPublishedDateStart,
        consentPublishedDateEnd,
        consentSignedDateStart,
        consentSignedDateEnd,
        isNotPublished,
        isNotSigned
    } = filter;


    return (
        <React.Fragment>
             <div className="row mb-3">
                <div className="col">
                    <ServiceAgreementsSelect
                        label="Service Terms"
                        isMulti={true}
                        defaultValue={serviceTermIds}
                        disableAnimations={true}
                        onBlur={(values) =>
                            setFilter(
                                "serviceTermIds",
                                values.map((el) => el.value)
                            )
                        }
                    />
                </div>
                <div className="col">
                    <FormSelect
                        label="CSLA Status"
                        options={[
                            {
                                label: "Drafting New Terms",
                                value: 0,
                                isDisabled: false
                            },
                            {
                                label: "Awaiting Signed Consent",
                                value: 2,
                            },
                            {
                                label: "Awaiting Service Fees Update",
                                value: 3,
                            },
                            {
                                label: "Service Agreement Operational",
                                value: 1,
                            },
                            {
                                label: "Instruction To Terminate",
                                value: 8,
                            },
                            {
                                label: "Service Agreement Terminated",
                                value: 4,
                            },
                            {
                                label: "Not Taken Up",
                                value: 5,
                            },
                            {
                                label: "Notification of Clients Death",
                                value: 9,
                            },
                            {
                                label: "Deceased Account",
                                value: 6,
                            },
                            {
                                label: "Fee Collection Paused",
                                value: 7,
                            }
                        ]}
                        isMulti
                        defaultValue={serviceTermStatusIds}
                        onBlur={(values) =>
                            setFilter(
                                "serviceTermStatusIds",
                                values.map((el) => el.value)
                            )
                        }
                    />
                </div>
            </div>
            <div className="row mb-3">
                <div className="col">
                    <MasterAccountSelect
                        label="Master Account"
                        isMulti
                        isClearable
                        value={masterAccountIds}
                        setValue={(selections) => setFilter("masterAccountIds", selections.map(({ value }) => value))}
                        disableAnimations
                    />
                </div>
                <div className="col">
                    <MasterAccountStatusSelect
                        label="Master Account Status"
                        isMulti={true}
                        defaultValue={masterAccountStatusIds}
                        disableAnimations={true}
                        onBlur={(values) =>
                            setFilter(
                                "masterAccountStatusIds",
                                values.map((el) => el.value)
                            )
                        }
                    />
                </div>
            </div>
            <div className="row mb-3">
                <div className="col-6">
                    <GenericAdviserSelect
                        label="Adviser"
                        isMulti={true}
                        defaultValue={adviserIds}
                        disableAnimations={true}
                        onBlur={(values) =>
                            setFilter(
                                "adviserIds",
                                values.map((el) => el.value)
                            )
                        }
                    />
                </div>
            </div>
            <div className="row mb-3">
                <div className="col">
                    <FormCheck
                        label={"Show not published"}
                        isChecked={isNotPublished}
                        onChange={(value) => setFilter("isNotPublished", !isNotPublished)}
                        disableAnimations
                    />
                </div>
            </div>
            <div className="row mb-3">
                <div className="col">
                    <DateInput
                        label="Published From"
                        isClearable
                        disabled={isNotPublished}
                        disableAnimations={true}
                        value={consentPublishedDateStart}
                        onBlur={(value, _) => setFilter("consentPublishedDateStart", value)}
                    />
                </div>
                <div className="col">
                    <DateInput
                        label="Published To"
                        isClearable
                        disabled={isNotPublished}
                        disableAnimations={true}
                        value={consentPublishedDateEnd}
                        onBlur={(value, _) => setFilter("consentPublishedDateEnd", value)}
                    />
                </div>
            </div>
            <div className="row mb-3">
                <div className="col">
                    <FormCheck
                        label={"Show not signed"}
                        isChecked={isNotSigned}
                        onChange={(value) => setFilter("isNotSigned", !isNotSigned)}
                        disableAnimations
                    />
                </div>
            </div>
            <div className="row mb-3">
                <div className="col">
                    <DateInput
                        label="Signed From"
                        isClearable
                        disabled={isNotSigned}
                        disableAnimations={true}
                        value={consentSignedDateStart}
                        onBlur={(value, _) => setFilter("consentSignedDateStart", value)}
                    />
                </div>
                <div className="col">
                    <DateInput
                        label="Signed To"
                        isClearable
                        disabled={isNotSigned}
                        disableAnimations={true}
                        value={consentSignedDateEnd}
                        onBlur={(value, _) => setFilter("consentSignedDateEnd", value)}
                    />
                </div>
            </div>
        </React.Fragment>
    );
};

export default ClientServiceTermsManagementFilters;
